import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
  PiScrollLight, PiArrowClockwiseLight, PiArrowSquareOutLight, PiTrashLight,
  PiWarningCircleLight, PiCheckCircleLight, PiSparkleLight, PiCalendarLight,
  PiClockCountdownLight, PiScalesLight, PiUsersLight, PiListChecksLight, PiCoinsLight,
  PiPencilSimpleLight, PiRepeatLight, PiInfoLight, PiShieldWarningLight, PiFileTextLight,
  PiGavelLight, PiHandshakeLight, PiReceiptLight, PiShieldCheckLight, PiUsersThreeLight,
  PiPlusLight, PiCircleLight, PiCheckCircleLight as PiCheckCircleFill, PiCalendarBlankLight,
  PiBriefcaseLight, PiBellLight, PiCalendarPlusLight, PiClockLight, PiExportLight,
  PiFileMagnifyingGlassLight,
} from 'react-icons/pi';
import DetailFrame from '../shared/Frames/DetailFrame';
import DetailTabBar, { TabItem } from '../shared/surface/DetailTabBar';
import PaneRail from '../shared/surface/PaneRail';
import DetailPageSurface, { DetailPane } from '../shared/surface/DetailPageSurface';
import GlassCard from '../shared/brand/GlassCard';
import { Field, FieldGrid, KpiTile, InfoRow, Well, FieldTone } from '../shared/brand/Field';
import {
  ContextSection, Chip, StatGrid, StatTile, TimelineRail, TimelineRow, PersonRow,
} from '../shared/brand/ContextPane';
import Badge from '../shared/TailwindComponents/Badge';
import Button from '../shared/TailwindComponents/Button';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import EmptyState from '../shared/TailwindComponents/EmptyState';
import AIUsagePanel from '../shared/aiUsage/AIUsagePanel';
import { isAiUsageEnabled } from '../../config/featureFlags';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import { showModal, hideModal } from '../../store/actions/modalActions';
import { showSidePanel, hideSidePanel } from '../../store/actions/sidePanelActions';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  getContractDetail, extractContract, unmarkContract, updateContract,
  getContractCollaborators, ContractCollaborator,
  addObligation, updateObligation, deleteObligation, ContractObligation,
  createRenewalTask, createObligationTask, createAllObligationTasks, linkContractMatter,
  getBrandingExportElements,
  ContractDetail as Detail, ContractRiskLevel,
} from '../../utils/contractsApi';
import ContractMatterPicker from './ContractMatterPicker';
import { buildContractCollaboratorAdapter } from '../../utils/collaboratorAdapters/contract';
import { buildFileNavigationPath } from '../../utils/fileNavigationUtils';
import { printContractSummary } from './exportSummary';
import { PromoteObligationForm } from './PromoteObligationModal';
import { ExportSummaryForm } from './ExportSummaryModal';
import { useContractPubSub } from './useContractPubSub';
import { useLicenseGate } from '../../hooks/useLicenseGate';
import { useFilesIn } from '../Files/useFilesIn';
import { useContractWorkspace, CONTRACT_SURFACE, ContractPanelId, ContractTab } from './surface';
import { buildTabPath, legacyTabRedirect, resolveTabSlug, tabFromUrl } from '../shared/surface/tabPath';

const INPUT = 'mt-1 w-full rounded-md border border-hairline bg-surface-sunken/50 px-3 py-2 text-sm text-fg-strong focus:border-champagne/60 focus:outline-none';

const STATUS: Record<string, { color: 'green' | 'yellow' | 'red' | 'gray'; tone: FieldTone; label: string }> = {
  active: { color: 'green', tone: 'success', label: 'Active' },
  expiring_soon: { color: 'yellow', tone: 'warning', label: 'Expiring soon' },
  expired: { color: 'red', tone: 'danger', label: 'Expired' },
  unknown: { color: 'gray', tone: 'default', label: 'No dates' },
};

// Labels for a linked task's status, reflected onto its obligation.
const OBL_TASK_STATUS: Record<string, { color: 'gray' | 'yellow' | 'green'; label: string }> = {
  todo: { color: 'gray', label: 'To do' },
  in_progress: { color: 'yellow', label: 'In progress' },
  done: { color: 'green', label: 'Done' },
  completed: { color: 'green', label: 'Done' },
};

const RISK: Record<ContractRiskLevel, { color: 'green' | 'yellow' | 'red'; tone: FieldTone; label: string }> = {
  low: { color: 'green', tone: 'success', label: 'Low risk' },
  medium: { color: 'yellow', tone: 'warning', label: 'Medium risk' },
  high: { color: 'red', tone: 'danger', label: 'High risk' },
};

const fmtDate = (iso: string | null | undefined) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return '—'; }
};

/** effective → today → expiry progress bar. */
const RenewalTimeline: React.FC<{ effective: string | null; expiry: string | null; status: string; days: number | null }>
  = ({ effective, expiry, status, days }) => {
    const pct = useMemo(() => {
      if (!effective || !expiry) return null;
      const e = new Date(effective).getTime();
      const x = new Date(expiry).getTime();
      if (!(x > e)) return null;
      return Math.max(0, Math.min(100, ((Date.now() - e) / (x - e)) * 100));
    }, [effective, expiry]);
    if (pct == null) return null;
    const bar = status === 'expired' ? 'bg-danger-500' : status === 'expiring_soon' ? 'bg-warning-500' : 'bg-success-500';
    return (
      <div className="mt-1">
        <div className="relative h-1.5 rounded-full bg-surface-sunken">
          <div className={`absolute inset-y-0 left-0 rounded-full ${bar}`} style={{ width: `${pct}%` }} />
          <div className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-fg-strong shadow" style={{ left: `${pct}%` }} />
        </div>
        <div className="mt-1.5 flex justify-between text-[11px] text-fg-muted">
          <span>{fmtDate(effective)}</span>
          <span className="font-medium text-fg-body">{days != null && days < 0 ? 'Overdue' : days != null ? `${days} ${days === 1 ? 'day' : 'days'} left` : ''}</span>
          <span>{fmtDate(expiry)}</span>
        </div>
      </div>
    );
  };

/** Section heading on the bare canvas (mirrors the matter "Movement" heading). */
const Section: React.FC<{ title: string; action?: React.ReactNode; children: React.ReactNode }>
  = ({ title, action, children }) => (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-[11px] font-medium text-fg-muted">{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );

const ContractDetail: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useAppDispatch();
  // "Start a review" on a contract that is a stored file (R4-09) - the same
  // playbook-picker flow as Files › Use in.
  const filesIn = useFilesIn({ t, dispatch, history });
  /* Extraction is matter AI (`matter_ai`); marking and editing are not. */
  const { hasMatterAiAccess, checkAiFeatureGate } = useLicenseGate();
  const extractionLocked = !hasMatterAiAccess();
  const { contractUuid, tab: tabParam } = useParams<{ contractUuid: string; tab?: string }>();
  const userId = useAppSelector((s: any) => s?.user?.userInfo?.UserID);
  const userUuid = useAppSelector((s: any) => s?.user?.userInfo?.UserUID);

  const [data, setData] = useState<Detail | null>(null);
  const [collaborators, setCollaborators] = useState<ContractCollaborator[]>([]);
  const [obligations, setObligations] = useState<ContractObligation[]>([]);
  const [newObl, setNewObl] = useState('');
  const [newOblDue, setNewOblDue] = useState('');
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    effective_date: '', expiry_date: '', renewal_notice_days: '', auto_renew: '',
    governing_law: '', contract_value: '', parties: '', key_obligations: '',
  });

  // Shared detail-page surface: page tabs + flexible panes, exactly like matters.
  // The active tab is a PATH segment (`/contracts/:contractUuid/:tab?`), so
  // refresh, deep link and browser back/forward all resolve here.
  const contractBasePath = `/contracts/${contractUuid}`;
  const { panels, tabs, activeTab, setActiveTab, goToTab } = useContractWorkspace(contractUuid, {
    initialTab: resolveTabSlug(CONTRACT_SURFACE.tabs, tabFromUrl(tabParam, location.search)),
    onTabChange: (tab) => history.push(buildTabPath(contractBasePath, tab)),
  });

  /* Old `?tab=<x>` deep links/bookmarks → the new path form, once, preserving
     any other query params. Only when there is no path tab, so no loop. */
  useEffect(() => {
    const target = legacyTabRedirect(contractBasePath, tabParam, location.search);
    if (target) history.replace(target);
  }, [tabParam, location.search, contractBasePath, history]);

  /* `?obligation=<uuid>` (Contracts › Obligations, R4-07): scroll to that
     obligation and hold a highlight on it, once it has loaded. */
  const focusObligation = new URLSearchParams(location.search).get('obligation');
  useEffect(() => {
    if (!focusObligation || activeTab !== 'Obligations') return;
    if (!obligations.some((o) => o.uuid === focusObligation)) return;
    const el = document.getElementById(`obligation-${focusObligation}`);
    el?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }, [focusObligation, activeTab, obligations]);

  /* Keep the surface's tab in step with the URL so back/forward steps tabs. */
  useEffect(() => {
    setActiveTab(resolveTabSlug(CONTRACT_SURFACE.tabs, tabFromUrl(tabParam, location.search)));
  }, [tabParam, location.search, setActiveTab]);

  const load = useCallback(async () => {
    try {
      setData(await getContractDetail(contractUuid));
      setNotFound(false);
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [contractUuid]);

  const loadCollaborators = useCallback(async () => {
    try {
      setCollaborators(await getContractCollaborators(contractUuid));
    } catch {
      // Non-fatal: sharing is best-effort and must never block the detail view.
    }
  }, [contractUuid]);

  useEffect(() => { load(); loadCollaborators(); }, [load, loadCollaborators]);

  // Open the shared manage-access side panel. Mirrors every other detail page:
  // a contract is shared through the generic CollaboratorService on the
  // 'contract' resource type (matter access cascades; direct invites layer on).
  const handleShare = useCallback(() => {
    const adapter = buildContractCollaboratorAdapter({
      contractUuid,
      collaborators,
      currentUserUuid: userUuid,
      canAdmin: true,
      onUpdate: () => { loadCollaborators(); },
    });
    dispatch(showSidePanel({
      type: 'manageResourceCollaborators',
      showSidePanel: true,
      size: 'md',
      message: t('Manage access'),
      icon: <PiUsersThreeLight className="w-5 h-5" />,
      onCancel: () => dispatch(hideSidePanel()),
      ...adapter,
    } as any));
  }, [contractUuid, collaborators, userUuid, dispatch, loadCollaborators, t]);

  // Obligations arrive on the detail payload (extracted ones are auto-promoted
  // to trackable rows server-side). Mirror into local state for optimistic edits.
  useEffect(() => { if (data?.obligations) setObligations(data.obligations); }, [data?.obligations]);

  // Suggest→promote: review/edit the task (title/description/priority/due) before
  // creating it, aligned with the matter suggested-task flow.
  const promoteObligation = useCallback((o: ContractObligation) => {
    dispatch(showModal({
      type: 'custom', showModal: true,
      title: t('Create task'), message: t('Create task'),
      icon: <PiCalendarPlusLight className="h-6 w-6 text-fg-body" />,
      content: (
        <PromoteObligationForm
          obligation={o}
          contractTitle={data?.title || ''}
          onCancel={() => dispatch(hideModal())}
          onCreate={async (overrides) => {
            try {
              const saved = await createObligationTask(contractUuid, o.uuid, overrides);
              setObligations((list) => list.map((x) => (x.uuid === o.uuid ? saved : x)));
              dispatch(hideModal());
              dispatch(showFeedbackModal({ showModal: true, modalType: 'success', duration: 3000, message: t('Task created and assigned to you.') }));
            } catch {
              dispatch(showFeedbackModal({ showModal: true, modalType: 'error', duration: 4000, message: t('Could not create the task.') }));
            }
          }}
        />
      ),
      onCancel: () => dispatch(hideModal()),
    } as any));
  }, [contractUuid, data?.title, dispatch, t]);

  const [promotingAll, setPromotingAll] = useState(false);
  const promoteAll = useCallback(async () => {
    setPromotingAll(true);
    try {
      const res = await createAllObligationTasks(contractUuid);
      setObligations(res.obligations);
      dispatch(showFeedbackModal({ showModal: true, modalType: 'success', duration: 3000, message: t('Created {{n}} task(s).', { n: res.created }) }));
    } catch {
      dispatch(showFeedbackModal({ showModal: true, modalType: 'error', duration: 4000, message: t('Could not create tasks.') }));
    } finally {
      setPromotingAll(false);
    }
  }, [contractUuid, dispatch, t]);

  const setObligationDue = useCallback(async (o: ContractObligation, due: string) => {
    try {
      const saved = await updateObligation(contractUuid, o.uuid, { due_date: due || null });
      setObligations((list) => list.map((x) => (x.uuid === o.uuid ? saved : x)));
    } catch {
      dispatch(showFeedbackModal({ showModal: true, modalType: 'error', duration: 3000, message: t('Could not set due date.') }));
    }
  }, [contractUuid, dispatch, t]);

  const addObligationRow = useCallback(async () => {
    const text = newObl.trim();
    if (!text) return;
    try {
      const created = await addObligation(contractUuid, text, newOblDue || null);
      setObligations((list) => [...list, created]);
      setNewObl(''); setNewOblDue('');
    } catch {
      dispatch(showFeedbackModal({ showModal: true, modalType: 'error', duration: 3000, message: t('Could not add obligation.') }));
    }
  }, [contractUuid, newObl, newOblDue, dispatch, t]);

  const removeObligationRow = useCallback(async (o: ContractObligation) => {
    setObligations((list) => list.filter((x) => x.uuid !== o.uuid));
    try {
      await deleteObligation(contractUuid, o.uuid);
    } catch {
      setObligations((list) => [...list, o]);  // revert
      dispatch(showFeedbackModal({ showModal: true, modalType: 'error', duration: 3000, message: t('Could not remove obligation.') }));
    }
  }, [contractUuid, dispatch, t]);

  // Live extraction updates. PubSub is the fast path; a fallback poll (below)
  // guarantees the view flips to the analysed state even when the socket can't
  // connect, so a user who opens a still-processing contract sees it finish
  // without reloading.
  const extracting = data?.extraction_status === 'pending' || data?.extraction_status === 'processing';
  useContractPubSub(contractUuid, !!extracting, useCallback(() => { load(); }, [load]), userId);
  useEffect(() => {
    if (!extracting) return;
    const id = setInterval(() => { load(); }, 5000);
    return () => clearInterval(id);
  }, [extracting, load]);

  const openSource = () => {
    if (!data) return;
    if (data.document_type === 'draft') {
      history.push(`/drafting/${data.document_uuid}`);
      return;
    }
    // File-type contract: deep-link straight to the file viewer. The backend
    // hands us the file's folder uuid; fall back to the Files root if unknown.
    history.push(buildFileNavigationPath(
      data.file_folder_uuid || undefined, undefined, data.document_uuid,
    ));
  };

  const retry = useCallback(async () => {
    if (extractionLocked) { checkAiFeatureGate('matter_ai'); return; }
    setBusy(true);
    try {
      await extractContract(contractUuid);
      dispatch(showFeedbackModal({ showModal: true, modalType: 'success', duration: 3000, message: t('Re-running extraction…') }));
      await load();
    } catch {
      dispatch(showFeedbackModal({ showModal: true, modalType: 'error', duration: 4000, message: t('Could not re-run extraction.') }));
    } finally { setBusy(false); }
  }, [contractUuid, dispatch, t, load, extractionLocked, checkAiFeatureGate]);

  const [creatingTask, setCreatingTask] = useState(false);
  const createRenewalTaskHandler = useCallback(async () => {
    setCreatingTask(true);
    try {
      await createRenewalTask(contractUuid);
      dispatch(showFeedbackModal({ showModal: true, modalType: 'success', duration: 3500, message: t('Renewal task created and assigned to you.') }));
    } catch {
      dispatch(showFeedbackModal({ showModal: true, modalType: 'error', duration: 4000, message: t('Could not create the renewal task.') }));
    } finally { setCreatingTask(false); }
  }, [contractUuid, dispatch, t]);

  const handleLinkMatter = useCallback(() => {
    dispatch(showModal({
      type: 'custom', showModal: true,
      title: t('Link a matter'), message: t('Link a matter'),
      icon: <PiBriefcaseLight className="h-6 w-6 text-fg-body" />,
      content: (
        <ContractMatterPicker
          currentMatterUuid={data?.matter_uuid || null}
          onCancel={() => dispatch(hideModal())}
          onPick={async (matterUuid) => {
            try {
              setData(await linkContractMatter(contractUuid, matterUuid));
              dispatch(hideModal());
              dispatch(showFeedbackModal({ showModal: true, modalType: 'success', duration: 2500, message: matterUuid ? t('Matter linked.') : t('Matter unlinked.') }));
            } catch {
              dispatch(showFeedbackModal({ showModal: true, modalType: 'error', duration: 4000, message: t('Could not update the matter link.') }));
            }
          }}
        />
      ),
      onCancel: () => dispatch(hideModal()),
    } as any));
  }, [contractUuid, data?.matter_uuid, dispatch, t]);

  const exportSummary = useCallback(() => {
    if (!data) return;
    // The branding choice lives in a ref the Dropdown updates, so the global
    // modal's own Export button (onConfirm) can read the latest selection.
    const choice = { templateId: null as string | null };
    dispatch(showModal({
      type: 'confirmation', showModal: true,
      title: t('Export summary'), message: t('Export summary'),
      confirmLabel: t('Export summary'), cancelLabel: t('Cancel'),
      icon: <PiExportLight className="h-6 w-6 text-fg-body" />,
      content: (
        <ExportSummaryForm onSelect={(id) => { choice.templateId = id; }} />
      ),
      onConfirm: async () => {
        let branding;
        try { branding = await getBrandingExportElements(choice.templateId); } catch { /* plain */ }
        printContractSummary(data, branding);
      },
      onCancel: () => dispatch(hideModal()),
    } as any));
  }, [data, dispatch, t]);

  const startEdit = () => {
    if (!data) return;
    setForm({
      effective_date: data.effective_date ? data.effective_date.slice(0, 10) : '',
      expiry_date: data.expiry_date ? data.expiry_date.slice(0, 10) : '',
      renewal_notice_days: data.renewal_notice_days != null ? String(data.renewal_notice_days) : '',
      auto_renew: data.auto_renew == null ? '' : data.auto_renew ? 'yes' : 'no',
      governing_law: data.governing_law || '',
      contract_value: data.contract_value || '',
      parties: (data.parties || []).map((p) => [p.name, p.role].filter(Boolean).join(' - ')).join('\n'),
      key_obligations: (data.key_obligations || []).join('\n'),
    });
    setEditing(true);
  };

  const saveEdit = useCallback(async () => {
    setBusy(true);
    try {
      const parties = form.parties.split('\n').map((l) => l.trim()).filter(Boolean).map((line) => {
        const parts = line.split(/\s+[—-]\s+/);
        return { name: (parts[0] || line).trim(), role: (parts[1] || '').trim() || undefined };
      });
      const key_obligations = form.key_obligations.split('\n').map((l) => l.trim()).filter(Boolean);
      await updateContract(contractUuid, {
        effective_date: form.effective_date || null,
        expiry_date: form.expiry_date || null,
        renewal_notice_days: form.renewal_notice_days ? Number(form.renewal_notice_days) : null,
        auto_renew: form.auto_renew === '' ? null : form.auto_renew === 'yes',
        governing_law: form.governing_law || null,
        contract_value: form.contract_value || null,
        parties,
        key_obligations,
      });
      dispatch(showFeedbackModal({ showModal: true, modalType: 'success', duration: 3000, message: t('Contract updated.') }));
      setEditing(false);
      await load();
    } catch {
      dispatch(showFeedbackModal({ showModal: true, modalType: 'error', duration: 4000, message: t('Could not save changes.') }));
    } finally { setBusy(false); }
  }, [contractUuid, form, dispatch, t, load]);

  const untrack = useCallback(() => {
    dispatch(showModal({
      type: 'confirmation', showModal: true, isDest: true,
      message: t('Stop tracking this contract?'),
      subMsg: t('It is removed from Contracts. The underlying document is untouched.'),
      icon: <PiTrashLight className="h-6 w-6 text-danger-600" />,
      onConfirm: async () => {
        dispatch(hideModal());
        try {
          await unmarkContract(contractUuid);
          history.push('/contracts');
        } catch {
          dispatch(showFeedbackModal({ showModal: true, modalType: 'error', duration: 4000, message: t('Could not untrack.') }));
        }
      },
      onCancel: () => dispatch(hideModal()),
    }));
  }, [contractUuid, dispatch, history, t]);

  const sb = data ? (STATUS[data.status] || STATUS.unknown) : STATUS.unknown;
  const rb = data?.risk_level ? RISK[data.risk_level] : null;
  const parties = (data?.parties || []).filter((p) => p?.name);
  const riskFlags = data?.risk_flags || [];
  const openObligations = obligations.filter((o) => o.status !== 'done' && o.status !== 'completed').length;
  const overdueObligations = obligations.filter((o) => o.overdue).length;
  const untrackedObligations = obligations.filter((o) => !o.tracked).length;
  const nextObligation = useMemo(() => obligations
    .filter((o) => o.status !== 'done' && o.due_date)
    .sort((a, b) => new Date(a.due_date!).getTime() - new Date(b.due_date!).getTime())[0] || null,
    [obligations]);

  // When the renewals engine will remind: notice window, 30 days out, 7 days out,
  // and the expiry itself - a plain view of what the nightly sweep will fire.
  const renewalPlan = useMemo(() => {
    if (!data?.expiry_date) return [] as { label: string; date: Date; past: boolean }[];
    const exp = new Date(data.expiry_date);
    const minus = (days: number) => { const d = new Date(exp); d.setDate(d.getDate() - days); return d; };
    const items: { label: string; date: Date }[] = [{ label: t('30 days before'), date: minus(30) }];
    if (data.renewal_notice_days) items.push({ label: t('Notice window ({{n}}d)', { n: data.renewal_notice_days }), date: minus(data.renewal_notice_days) });
    items.push({ label: t('7 days before'), date: minus(7) });
    items.push({ label: t('Expiry'), date: exp });
    return items
      .map((i) => ({ ...i, past: i.date.getTime() < Date.now() }))
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [data?.expiry_date, data?.renewal_notice_days, t]);

  // Activity timeline - what the engine has done, newest first: reminders fired
  // plus the AI analysis. (Contract volumes are small; this is all in the payload.)
  const reminderLabel = useCallback((stage: string) => ({
    renewal_d30: t('30-day renewal reminder sent'),
    renewal_notice: t('Renewal-notice reminder sent'),
    renewal_d7: t('7-day renewal reminder sent'),
    expired: t('Expiry reminder sent'),
  } as Record<string, string>)[stage] || t('Reminder sent'), [t]);

  const activity = useMemo(() => {
    const items: { label: string; date: string }[] = [];
    (data?.reminders || []).forEach((r) => { if (r.sent_at) items.push({ label: reminderLabel(r.stage), date: r.sent_at }); });
    if (data?.extracted_at) items.push({ label: t('Analysed by PONS'), date: data.extracted_at });
    return items.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [data?.reminders, data?.extracted_at, reminderLabel, t]);
  const daysLabel = data?.days_to_expiry == null ? '—' : data.days_to_expiry < 0 ? t('Overdue') : (data.days_to_expiry === 1 ? t('{{n}} day', { n: 1 }) : t('{{n}} days', { n: data.days_to_expiry }));

  // ── Tab bar items ────────────────────────────────────────────────────────
  const tabItems: TabItem[] = useMemo(() => {
    const meta: Record<ContractTab, { label: string; icon: React.ReactNode }> = {
      Overview: { label: t('Overview'), icon: <PiFileTextLight /> },
      Obligations: { label: t('Terms & obligations'), icon: <PiListChecksLight /> },
      Risk: { label: t('Risk & alignment'), icon: <PiShieldWarningLight /> },
    };
    return tabs.map((v) => ({ value: v, label: meta[v].label, icon: meta[v].icon }));
  }, [tabs, t]);

  // ── Panes (left Context, right AI usage) ─────────────────────────────────
  const contextBody = () => {
    if (!data) return null;
    return (
      <div className="p-3 space-y-5">
        <section>
          <ContextSection>{t('Overview')}</ContextSection>
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge color={sb.color} className="text-[11px] capitalize">{t(sb.label)}</Badge>
            {rb && <Badge color={rb.color} className="text-[11px]">{t(rb.label)}</Badge>}
            {data.contract_type && <Chip icon={<PiScrollLight />} title={data.contract_type}>{data.contract_type}</Chip>}
            {data.governing_law && <Chip icon={<PiScalesLight />}>{data.governing_law}</Chip>}
          </div>
        </section>

        <section>
          <ContextSection>{t('At a glance')}</ContextSection>
          <GlassCard padding="none">
            <StatGrid>
              <StatTile icon={<PiUsersLight />} label={t('Parties')} value={parties.length} />
              <StatTile icon={<PiListChecksLight />} label={t('Obligations')} value={obligations.length} />
              <StatTile icon={<PiShieldWarningLight />} label={t('Risk flags')} value={riskFlags.length} />
              <StatTile icon={<PiScalesLight />} label={t('Open findings')} value={data.risk?.open_findings || 0}
                onClick={data.risk?.open_findings ? () => history.push('/reviews') : undefined} />
            </StatGrid>
          </GlassCard>
        </section>

        <section>
          <ContextSection>{t('Timeline')}</ContextSection>
          <TimelineRail>
            <TimelineRow label={t('Effective')} value={data.effective_date ? fmtDate(data.effective_date) : undefined} />
            <TimelineRow label={t('Expiry')} value={data.expiry_date ? fmtDate(data.expiry_date) : undefined} />
            <TimelineRow label={t('Renewal notice')} value={data.renewal_notice_days != null ? (data.renewal_notice_days === 1 ? t('{{n}} day', { n: 1 }) : t('{{n}} days', { n: data.renewal_notice_days })) : undefined} />
            <TimelineRow label={t('Extracted')} value={data.extracted_at ? fmtDate(data.extracted_at) : undefined} />
          </TimelineRail>
        </section>

        <section>
          <ContextSection>{t('Parties')}</ContextSection>
          {parties.length === 0 ? (
            <Well><span className="text-xs text-fg-muted">{t('No parties extracted yet.')}</span></Well>
          ) : (
            <div className="divide-y divide-hairline-soft">
              {parties.map((p, i) => (
                <PersonRow key={i} name={p.name || ''} sideLabel={p.role || t('Party')} tone={i === 0 ? 'accent' : 'info'} />
              ))}
            </div>
          )}
        </section>
      </div>
    );
  };

  const panes: DetailPane<ContractPanelId>[] = [
    { id: 'context', title: t('Context'), icon: <PiInfoLight />, body: contextBody },
    ...(isAiUsageEnabled() ? [{
      id: 'aiUsage' as ContractPanelId, title: t('AI usage'), icon: <PiSparkleLight />,
      body: () => (
        <AIUsagePanel entityType="contract" entityId={contractUuid} entityName={data?.title || ''}
          onClose={() => panels.setVisible('aiUsage', false)} />
      ),
    }] : []),
  ];

  const railItems = CONTRACT_SURFACE.railItems({
    t,
    icons: { context: <PiInfoLight />, aiUsage: <PiSparkleLight /> },
  });

  // ── Canvas ───────────────────────────────────────────────────────────────
  const renderCanvas = () => {
    if (loading) return <Skeleton variant="detail" />;
    if (notFound || !data) {
      return (
        <GlassCard>
          <EmptyState icon={<PiScrollLight />} headline={t('Contract not found')} subtext={t('It may have been untracked.')} />
        </GlassCard>
      );
    }
    if (editing) return renderEditForm();

    if (activeTab === 'Obligations') return renderObligations();
    if (activeTab === 'Risk') return renderRisk();
    return renderOverview();
  };

  const renderOverview = () => {
    if (!data) return null;
    return (
      <>
        {/* Hero */}
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1 space-y-2.5">
            <h1 className="text-3xl font-normal text-fg-strong leading-tight tracking-tight break-words">{data.title}</h1>
            <div className="flex flex-wrap items-center gap-2">
              <Badge color={sb.color} className="capitalize text-[11px]">{t(sb.label)}</Badge>
              {rb && <Badge color={rb.color} className="text-[11px]">{t(rb.label)}</Badge>}
              {data.contract_type && <Badge color="gray" className="text-[11px]">{data.contract_type}</Badge>}
              {data.governing_law && (
                <span className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-hairline bg-surface-sunken/70 px-2.5 py-0.5 text-xs text-fg-body">
                  <PiScalesLight className="h-3.5 w-3.5" /><span className="truncate">{data.governing_law}</span>
                </span>
              )}
              {data.matter_uuid ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-hairline bg-surface-sunken/70 pl-2.5 pr-1 py-0.5 text-xs text-fg-body">
                  <button type="button" onClick={() => history.push(`/matters/${data.matter_uuid}`)} className="inline-flex items-center gap-1.5 hover:text-champagne">
                    <PiBriefcaseLight className="h-3.5 w-3.5" />{t('Linked matter')}
                  </button>
                  <button type="button" onClick={handleLinkMatter} title={t('Change matter')} className="ml-0.5 rounded-full px-1 text-fg-muted hover:text-fg-body">
                    <PiPencilSimpleLight className="h-3 w-3" />
                  </button>
                </span>
              ) : (
                <button type="button" onClick={handleLinkMatter}
                  className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-hairline px-2.5 py-0.5 text-xs text-fg-muted hover:text-fg-body hover:border-champagne/50">
                  <PiBriefcaseLight className="h-3.5 w-3.5" />{t('Link a matter')}
                </button>
              )}
              {extracting && (
                <span className="inline-flex items-center gap-1 text-xs text-fg-muted"><PiSparkleLight className="h-3.5 w-3.5 animate-pulse" />{t('Extracting…')}</span>
              )}
            </div>
          </div>
        </header>

        {/* Live re-analysis banner - makes the processing state obvious (the
            tiny header chip is easy to miss) and reassures that nothing the user
            owns is dropped while PONS re-reads the full document. */}
        {extracting && (
          <div className="flex items-start gap-3 rounded-xl border border-champagne/40 bg-champagne/[0.06] px-4 py-3">
            <PiSparkleLight className="mt-0.5 h-4 w-4 shrink-0 animate-pulse text-champagne" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-fg-body">{t('Re-analyzing this contract…')}</p>
              <p className="text-xs text-fg-muted">{t('PONS is reading the full document. Facts below refresh automatically when it finishes — your tasks, tracked obligations and linked matter are kept.')}</p>
            </div>
          </div>
        )}

        {/* KPI strip */}
        <GlassCard padding="none">
          <div className="details-kpis divide-hairline-soft">
            <KpiTile icon={<PiClockCountdownLight />} label={t('Time to expiry')} value={daysLabel} tone={sb.tone} />
            <KpiTile icon={<PiCoinsLight />} label={t('Contract value')} value={data.contract_value || '—'} />
            <KpiTile icon={<PiUsersLight />} label={t('Parties')} value={parties.length || '—'} />
            <KpiTile icon={<PiShieldWarningLight />} label={t('Risk')} value={rb ? t(rb.label) : '—'} tone={rb?.tone || 'default'} />
          </div>
        </GlassCard>

        <RenewalTimeline effective={data.effective_date} expiry={data.expiry_date} status={data.status} days={data.days_to_expiry} />

        {/* Summary - the star. Bare on the canvas, like the matter summary. */}
        <Section title={t('Summary')} action={
          <Button variant="neutral" size="small" onClick={retry} isLoading={busy || extracting} disabled={extracting || extractionLocked}
            tooltip={extractionLocked ? t('AI analysis is not included in your plan. Upgrade to unlock.') : ''}
            icon={<PiArrowClockwiseLight className="h-3.5 w-3.5" />}>
            {extracting ? t('Analyzing…') : data.extraction_status === 'failed' ? t('Retry') : t('Re-analyze')}
          </Button>
        }>
          {data.summary ? (
            <p className="text-[15px] leading-relaxed text-fg-body whitespace-pre-wrap break-words">{data.summary}</p>
          ) : extracting ? (
            <p className="text-sm text-fg-muted">{t('PONS is reading this contract — the summary will appear here shortly.')}</p>
          ) : data.extraction_status === 'failed' ? (
            <Well><span className="text-sm text-fg-muted">{t('Analysis failed. Try re-analyzing.')}</span></Well>
          ) : (
            <p className="text-sm text-fg-muted">{t('No summary yet.')}</p>
          )}
        </Section>

        {/* Key terms + parties */}
        <div className="details-body gap-6 items-start">
          <div className="min-w-0 space-y-8">
            <Section title={t('Key terms')}>
              <GlassCard>
                {data.source_truncated && !extracting && (
                  <div className="mb-3 flex items-start gap-2 rounded-md border border-warning-300/60 bg-warning-50/60 px-3 py-2 text-[12px] text-warning-700 dark:border-warning-700/50 dark:bg-warning-900/20 dark:text-warning-300">
                    <PiWarningCircleLight className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>{t('This document was long — only part of it was analysed, so the dates may be incomplete. Please verify them.')}</span>
                  </div>
                )}
                <FieldGrid columns={3} measure="container">
                  <Field icon={<PiCalendarLight />} label={t('Effective')} value={fmtDate(data.effective_date)} />
                  <Field icon={<PiClockCountdownLight />} label={t('Expiry')} value={fmtDate(data.expiry_date)} />
                  <Field icon={<PiScalesLight />} label={t('Governing law')} value={data.governing_law || undefined} />
                  <Field icon={<PiCoinsLight />} label={t('Value')} value={data.contract_value || undefined} />
                  <Field icon={<PiRepeatLight />} label={t('Auto-renew')} value={data.auto_renew == null ? undefined : data.auto_renew ? t('Yes') : t('No')} />
                  <Field icon={<PiCalendarLight />} label={t('Renewal notice')} value={data.renewal_notice_days != null ? (data.renewal_notice_days === 1 ? t('{{n}} day', { n: 1 }) : t('{{n}} days', { n: data.renewal_notice_days })) : undefined} />
                </FieldGrid>
                {data.term_description && (
                  <p className="mt-4 text-sm leading-relaxed text-fg-body border-t border-hairline-soft pt-3">{data.term_description}</p>
                )}
              </GlassCard>
            </Section>

            {renewalPlan.length > 0 && (
              <Section title={t('Renewal plan')}>
                <GlassCard icon={<PiBellLight />} title={t('When PONS will remind the team')}>
                  <TimelineRail>
                    {renewalPlan.map((m, i) => (
                      <TimelineRow key={i}
                        label={m.label}
                        value={`${fmtDate(m.date.toISOString())}${m.past ? ` · ${t('passed')}` : ''}`}
                      />
                    ))}
                  </TimelineRail>
                  <p className="mt-3 text-[11px] text-fg-muted">
                    {t('Reminders go to everyone with access to this contract.')}
                  </p>
                  <div className="mt-3 border-t border-hairline-soft pt-3">
                    <Button variant="outline" size="small" onClick={createRenewalTaskHandler} isLoading={creatingTask}
                      icon={<PiCalendarPlusLight className="h-3.5 w-3.5" />}>
                      {t('Create renewal task')}
                    </Button>
                  </div>
                </GlassCard>
              </Section>
            )}

            {activity.length > 0 && (
              <Section title={t('Activity')}>
                <GlassCard icon={<PiClockLight />} title={t('Recent activity')}>
                  <ul className="space-y-2.5">
                    {activity.map((a, i) => (
                      <li key={i} className="flex items-center justify-between gap-3 text-sm">
                        <span className="text-fg-body">{a.label}</span>
                        <span className="shrink-0 text-[11px] text-fg-muted">{fmtDate(a.date)}</span>
                      </li>
                    ))}
                  </ul>
                </GlassCard>
              </Section>
            )}
          </div>
          <div className="details-side min-w-0 space-y-4">
            <Section title={t('Obligations')} action={
              <button type="button" onClick={() => goToTab('Obligations')}
                className="text-[11px] text-fg-muted hover:text-fg-body">{t('View all')}</button>
            }>
              <GlassCard padding="none">
                <StatGrid>
                  <StatTile icon={<PiListChecksLight />} label={t('Open')} value={openObligations} />
                  <StatTile icon={<PiWarningCircleLight />} label={t('Overdue')} value={overdueObligations} />
                </StatGrid>
                {nextObligation && (
                  <div className="border-t border-hairline-soft px-3 py-2.5">
                    <div className="text-[11px] text-fg-muted">{t('Next due')}</div>
                    <div className="mt-0.5 truncate text-sm text-fg-body">{nextObligation.description}</div>
                    <div className="text-[11px] text-fg-muted">{fmtDate(nextObligation.due_date)}</div>
                  </div>
                )}
              </GlassCard>
            </Section>
            <Section title={t('Parties')}>
              <GlassCard>
                {parties.length === 0 ? (
                  <p className="text-sm text-fg-muted">{t('No parties extracted yet.')}</p>
                ) : (
                  <div className="space-y-2">
                    {parties.map((p, i) => (
                      <Well key={i} className="flex items-center justify-between">
                        <span className="text-sm font-medium text-fg-body">{p.name}</span>
                        {p.role && <span className="text-xs text-fg-muted">{p.role}</span>}
                      </Well>
                    ))}
                  </div>
                )}
              </GlassCard>
            </Section>
          </div>
        </div>
      </>
    );
  };

  const renderObligations = () => {
    if (!data) return null;
    return (
      <div className="details-body gap-6 items-start">
        <div className="min-w-0 space-y-8">
          <Section
            title={t('Obligations')}
            action={untrackedObligations > 0 ? (
              <Button variant="outline" size="small" onClick={promoteAll} isLoading={promotingAll}
                icon={<PiListChecksLight className="h-3.5 w-3.5" />}>
                {t('Create tasks for all ({{n}})', { n: untrackedObligations })}
              </Button>
            ) : undefined}
          >
            <p className="mb-3 text-[13px] text-fg-muted">
              {t('Obligations are what this contract requires. Create a task to assign one and track it to done — the task is where status lives.')}
            </p>
            <GlassCard icon={<PiListChecksLight />} title={t('What this contract requires')}>
              {obligations.length === 0 ? (
                <p className="text-sm text-fg-muted">{t('No obligations yet. Add one below, or re-analyze to extract them.')}</p>
              ) : (
                <ul className="divide-y divide-hairline-soft">
                  {obligations.map((o) => {
                    const st = (o.task?.status || o.status || 'todo');
                    const stMeta = OBL_TASK_STATUS[st] || OBL_TASK_STATUS.todo;
                    return (
                      <li key={o.uuid} id={`obligation-${o.uuid}`}
                        aria-current={focusObligation === o.uuid ? 'true' : undefined}
                        className={`group flex items-start gap-3 py-2.5 ${focusObligation === o.uuid ? '-mx-2 rounded-md bg-champagne/10 px-2 ring-1 ring-champagne/40' : ''}`}>
                        <span className="mt-0.5 shrink-0 text-fg-muted/50">
                          {o.tracked ? <PiCheckCircleFill size={16} className={stMeta.color === 'green' ? 'text-success-500' : 'text-fg-muted/50'} /> : <PiCircleLight size={16} />}
                        </span>
                        <div className="min-w-0 flex-1">
                          <span className="text-sm text-fg-body">{o.description}</span>
                          <div className="mt-1 flex flex-wrap items-center gap-2">
                            {o.tracked ? (
                              <>
                                <Badge color={stMeta.color} className="text-[10px]">{t('Task')} · {t(stMeta.label)}</Badge>
                                {o.due_date && <span className="inline-flex items-center gap-1 text-[11px] text-fg-muted"><PiCalendarBlankLight size={12} />{fmtDate(o.due_date)}</span>}
                                {o.overdue && <Badge color="red" className="text-[10px]">{t('Overdue')}</Badge>}
                                {o.task_uuid && (
                                  <button type="button" onClick={() => history.push(`/tasks/${o.task_uuid}`)}
                                    className="inline-flex items-center gap-1 text-[11px] text-champagne hover:underline">
                                    {t('Open task')}<PiArrowSquareOutLight size={11} />
                                  </button>
                                )}
                              </>
                            ) : (
                              <>
                                <span className="text-[11px] text-fg-muted">{t('Not tracked yet')}</span>
                                <label className="inline-flex items-center gap-1 text-[11px] text-fg-muted">
                                  <PiCalendarBlankLight size={12} />
                                  <input type="date" value={o.due_date ? o.due_date.slice(0, 10) : ''}
                                    onChange={(e) => setObligationDue(o, e.target.value)}
                                    className="rounded border border-hairline bg-surface-sunken/50 px-1.5 py-0.5 text-[11px] text-fg-body focus:border-champagne/60 focus:outline-none" />
                                </label>
                                <Button variant="outline" size="small" onClick={() => promoteObligation(o)}
                                  icon={<PiCalendarPlusLight className="h-3 w-3" />}>
                                  {t('Create task')}
                                </Button>
                                {o.source === 'extracted' && <span className="text-[10px] text-fg-subtle">{t('from PONS')}</span>}
                              </>
                            )}
                          </div>
                        </div>
                        <button type="button" onClick={() => removeObligationRow(o)}
                          className="opacity-0 group-hover:opacity-100 text-fg-subtle hover:text-danger-500" title={t('Remove obligation')}>
                          <PiTrashLight size={15} />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
              <div className="mt-3 flex items-center gap-2 border-t border-hairline-soft pt-3">
                <input type="text" value={newObl} onChange={(e) => setNewObl(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') addObligationRow(); }}
                  placeholder={t('Add an obligation…')}
                  className="min-w-0 flex-1 rounded-md border border-hairline bg-surface-sunken/50 px-3 py-1.5 text-sm text-fg-body focus:border-champagne/60 focus:outline-none" />
                <Button variant="outline" size="small" onClick={addObligationRow} icon={<PiPlusLight className="h-3.5 w-3.5" />} disabled={!newObl.trim()}>
                  {t('Add')}
                </Button>
              </div>
            </GlassCard>
          </Section>
        </div>
        <div className="details-side min-w-0 space-y-4">
          <Section title={t('Commercial terms')}>
            <GlassCard icon={<PiReceiptLight />} title={t('Payment')}>
              <p className="text-sm leading-relaxed text-fg-body">{data.payment_terms || t('Not stated.')}</p>
            </GlassCard>
            <GlassCard icon={<PiHandshakeLight />} title={t('Termination')}>
              <p className="text-sm leading-relaxed text-fg-body">{data.termination_summary || t('Not stated.')}</p>
            </GlassCard>
            <GlassCard icon={<PiGavelLight />} title={t('Dispute resolution')}>
              <p className="text-sm leading-relaxed text-fg-body">{data.dispute_resolution || t('Not stated.')}</p>
            </GlassCard>
          </Section>
        </div>
      </div>
    );
  };

  const renderRisk = () => {
    if (!data) return null;
    return (
      <>
        <div className="details-body gap-6 items-start">
          <div className="min-w-0 space-y-8">
            {/* What this tab is, and what can be done from it (R4-09). */}
            <p className="text-[13px] text-fg-muted">
              {t('An overview of the risks PONS found in this contract. Re-analyze after the contract changes, or start a review for a clause-by-clause check against a playbook.')}
            </p>
            <Section title={t('Risk assessment')} action={
              <span className="flex items-center gap-2">
                {rb && <Badge color={rb.color} className="text-[11px]">{t(rb.label)}</Badge>}
                <Button variant="neutral" size="small" onClick={retry} isLoading={busy || extracting} disabled={extracting || extractionLocked}
                  tooltip={extractionLocked
                    ? t('AI analysis is not included in your plan. Upgrade to unlock.')
                    : t('Re-runs the whole analysis - dates, parties, obligations and risk.')}
                  icon={<PiArrowClockwiseLight className="h-3.5 w-3.5" />}>
                  {extracting ? t('Analyzing…') : t('Re-analyze')}
                </Button>
              </span>
            }>
              {data.risk_summary ? (
                <>
                  <p className="text-[15px] leading-relaxed text-fg-body whitespace-pre-wrap break-words">{data.risk_summary}</p>
                  <p className="mt-2 inline-flex items-center gap-1 text-[11px] text-fg-muted">
                    <PiSparkleLight className="h-3 w-3" />
                    {t('Generated by PONS — review before relying on it.')}
                  </p>
                </>
              ) : (
                <p className="text-sm text-fg-muted">{t('No risk assessment yet.')}</p>
              )}
            </Section>

            {riskFlags.length > 0 && (
              <Section title={t('Flagged clauses')}>
                <div className="space-y-2.5">
                  {riskFlags.map((f, i) => {
                    const sev = (f.severity || 'medium') as ContractRiskLevel;
                    const c = RISK[sev] || RISK.medium;
                    return (
                      <GlassCard key={i}>
                        <div className="flex items-start gap-3">
                          <PiShieldWarningLight className={`mt-0.5 shrink-0 ${c.tone === 'danger' ? 'text-danger-500' : c.tone === 'warning' ? 'text-warning-500' : 'text-success-500'}`} size={18} />
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-fg-strong">{f.title}</span>
                              <Badge color={c.color} className="text-[10px] capitalize">{t(sev)}</Badge>
                            </div>
                            {f.detail && <p className="mt-0.5 text-sm text-fg-body">{f.detail}</p>}
                          </div>
                        </div>
                      </GlassCard>
                    );
                  })}
                </div>
              </Section>
            )}
          </div>

          <div className="details-side min-w-0 space-y-4">
            <Section title={t('Alignment')}>
              <GlassCard icon={<PiShieldCheckLight />} title={t('Source alignment')}>
                {data.intelligence_status === 'behind' ? (
                  <Badge color="yellow" className="inline-flex items-center gap-1 text-[11px]"><PiWarningCircleLight size={12} />{t('Behind its template/branding')}</Badge>
                ) : data.intelligence_status === 'aligned' ? (
                  <Badge color="green" className="inline-flex items-center gap-1 text-[11px]"><PiCheckCircleLight size={12} />{t('Aligned')}</Badge>
                ) : (
                  <span className="text-sm text-fg-muted">{t('Not tracked')}</span>
                )}
              </GlassCard>
              <GlassCard icon={<PiScalesLight />} title={t('Review risk')}>
                {data.risk?.open_findings ? (
                  <button type="button" onClick={() => history.push('/reviews')}
                    className="inline-flex items-center gap-1 text-sm font-medium text-danger-600 hover:underline dark:text-danger-400">
                    <PiWarningCircleLight size={14} />{t('{{n}} open findings', { n: data.risk.open_findings })}
                  </button>
                ) : data.risk?.reviewed ? (
                  <Badge color="green" className="inline-flex items-center gap-1 text-[11px]"><PiCheckCircleLight size={12} />{t('No open findings')}</Badge>
                ) : (
                  <span className="text-sm text-fg-muted">{t('Not reviewed')}</span>
                )}
                {data.document_type === 'file' && data.document_uuid && (
                  <div className="mt-3">
                    <Button variant="outline" size="small"
                      icon={<PiFileMagnifyingGlassLight className="h-3.5 w-3.5" />}
                      onClick={() => filesIn.actionsFor({ uuids: [data.document_uuid], names: [data.title] })
                        .find((a) => a.key === 'review')?.onClick()}>
                      {t('Start a review')}
                    </Button>
                  </div>
                )}
              </GlassCard>
            </Section>
          </div>
        </div>
      </>
    );
  };

  const renderEditForm = () => (
    <GlassCard icon={<PiPencilSimpleLight />} title={t('Edit details')}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="text-[11px] font-medium text-fg-muted">{t('Effective date')}
          <input type="date" value={form.effective_date} onChange={(e) => setForm((f) => ({ ...f, effective_date: e.target.value }))} className={INPUT} />
        </label>
        <label className="text-[11px] font-medium text-fg-muted">{t('Expiry date')}
          <input type="date" value={form.expiry_date} onChange={(e) => setForm((f) => ({ ...f, expiry_date: e.target.value }))} className={INPUT} />
        </label>
        <label className="text-[11px] font-medium text-fg-muted">{t('Renewal notice (days)')}
          <input type="number" min={0} value={form.renewal_notice_days} onChange={(e) => setForm((f) => ({ ...f, renewal_notice_days: e.target.value }))} className={INPUT} />
        </label>
        <label className="text-[11px] font-medium text-fg-muted">{t('Auto-renew')}
          <select value={form.auto_renew} onChange={(e) => setForm((f) => ({ ...f, auto_renew: e.target.value }))} className={INPUT}>
            <option value="">{t('Unknown')}</option>
            <option value="yes">{t('Yes')}</option>
            <option value="no">{t('No')}</option>
          </select>
        </label>
        <label className="text-[11px] font-medium text-fg-muted">{t('Governing law')}
          <input type="text" value={form.governing_law} onChange={(e) => setForm((f) => ({ ...f, governing_law: e.target.value }))} className={INPUT} />
        </label>
        <label className="text-[11px] font-medium text-fg-muted">{t('Value')}
          <input type="text" value={form.contract_value} onChange={(e) => setForm((f) => ({ ...f, contract_value: e.target.value }))} className={INPUT} placeholder={t('e.g. EUR 250,000 / year')} />
        </label>
      </div>
      <label className="mt-3 block text-[11px] font-medium text-fg-muted">{t('Parties — one per line, "Name — Role"')}
        <textarea rows={3} value={form.parties} onChange={(e) => setForm((f) => ({ ...f, parties: e.target.value }))} className={INPUT} />
      </label>
      <label className="mt-3 block text-[11px] font-medium text-fg-muted">{t('Key obligations — one per line')}
        <textarea rows={4} value={form.key_obligations} onChange={(e) => setForm((f) => ({ ...f, key_obligations: e.target.value }))} className={INPUT} />
      </label>
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="outline" size="small" onClick={() => setEditing(false)} disabled={busy}>{t('Cancel')}</Button>
        <Button variant="primary" size="small" onClick={saveEdit} isLoading={busy}>{t('Save changes')}</Button>
      </div>
    </GlassCard>
  );

  return (
    <DetailFrame
      breadcrumbs={[
        { name: t('Contracts'), href: '/contracts', current: false },
        { name: data?.title || t('Contract'), href: `/contracts/${contractUuid}`, current: true },
      ]}
      onBack={() => history.push('/contracts')}
      collaborators={collaborators.map((c) => ({
        user_uuid: c.collaborator_id || c.uuid || '',
        name: c.name || c.email || '',
        initials: c.initials || (c.name || c.email || '?').slice(0, 2).toUpperCase(),
        access: c.access || 'view',
        email: c.email,
        avatar_url: c.avatar_url,
      }))}
      actions={data && !editing ? [
        { label: t('Edit details'), onClick: startEdit, icon: <PiPencilSimpleLight className="w-4 h-4" />, variant: 'primary' as const },
      ] : []}
      dropdownActions={data ? [
        { label: t('Share'), onClick: handleShare, icon: <PiUsersThreeLight className="w-4 h-4" />, variant: 'outline' as const },
        { label: t('Export summary'), onClick: exportSummary, icon: <PiExportLight className="w-4 h-4" />, variant: 'outline' as const },
        { label: t('Open document'), onClick: openSource, icon: <PiArrowSquareOutLight className="w-4 h-4" />, variant: 'outline' as const },
        { label: t('Untrack'), onClick: untrack, icon: <PiTrashLight className="w-4 h-4" />, variant: 'neutral' as const },
      ] : []}
    >
      <div className="flex flex-col w-full h-full overflow-hidden">
        {/* Page tabs (left) + flexible pane toggles (right) - one row. */}
        <div className="flex-shrink-0 w-full">
          <DetailTabBar
            tabs={tabItems}
            activeTab={activeTab}
            onTabClick={goToTab}
            showLabels
            actions={<PaneRail panels={panels} ariaLabel={t('Panels')} labels="auto" items={railItems} />}
          />
        </div>

        <DetailPageSurface surface={CONTRACT_SURFACE} layoutApi={panels} panes={panes}>
          <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main">
            <div className="details-canvas w-full">
              <div className="w-full px-6 py-6 space-y-7">
                {renderCanvas()}
              </div>
            </div>
          </div>
        </DetailPageSurface>
      </div>
    </DetailFrame>
  );
};

export default ContractDetail;
